import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import SuburbAutocomplete from '../components/SuburbAutocomplete';
import {
  ChevronRight, ChevronDown, MapPin, LayoutGrid, PlusCircle, Trash2, Save, X, ArrowLeft,
} from 'lucide-react';

const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];
const RULE_TYPES = [
  { value: 'national',        label: 'National coverage' },
  { value: 'state',           label: 'State' },
  { value: 'postcode_list',   label: 'Postcode list' },
  { value: 'postcode_prefix', label: 'Postcode prefix' },
  { value: 'radius',          label: 'Radius from postcode' },
];

// ---------------------------------------------------------------------------
// Framework administration — categories/services activation + location rules
// ---------------------------------------------------------------------------

export default function AdminFrameworkPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('taxonomy');

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <button
              onClick={() => navigate('/admin')}
              className="text-xs text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 mb-2"
              data-testid="framework-back"
            >
              <ArrowLeft className="w-3 h-3" /> Admin
            </button>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-gray-900">
              Framework administration
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Retire categories or services and manage reusable location rules without a deploy.
            </p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} data-testid="framework-tabs">
          <TabsList className="bg-gray-100 p-1">
            <TabsTrigger value="taxonomy" className="data-[state=active]:bg-white" data-testid="tab-taxonomy">
              <LayoutGrid className="w-4 h-4 mr-2" /> Categories & Services
            </TabsTrigger>
            <TabsTrigger value="locations" className="data-[state=active]:bg-white" data-testid="tab-locations">
              <MapPin className="w-4 h-4 mr-2" /> Location Rules
            </TabsTrigger>
          </TabsList>

          <TabsContent value="taxonomy" className="mt-6">
            <TaxonomyPanel />
          </TabsContent>
          <TabsContent value="locations" className="mt-6">
            <LocationRulesPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Taxonomy — toggle categories & services on/off (persists to server)
// ---------------------------------------------------------------------------

const TaxonomyPanel = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/framework/categories');
      setCategories(res.data.categories || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load taxonomy');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleCategory = async (cat, next) => {
    setSaving(s => ({ ...s, [cat.category_id]: true }));
    try {
      await api.patch(`/admin/framework/categories/${cat.category_id}`, { active: next });
      setCategories(cs => cs.map(c => c.category_id === cat.category_id ? { ...c, active: next } : c));
      toast.success(next ? 'Category activated' : 'Category retired');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update category');
    } finally {
      setSaving(s => ({ ...s, [cat.category_id]: false }));
    }
  };

  const toggleService = async (cat, svcId, next) => {
    const key = `${cat.category_id}:${svcId}`;
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await api.patch(`/admin/framework/categories/${cat.category_id}`, {
        service_overrides: { [svcId]: { active: next } },
      });
      setCategories(cs => cs.map(c => {
        if (c.category_id !== cat.category_id) return c;
        return {
          ...c,
          services: { ...c.services, [svcId]: { ...c.services[svcId], active: next } },
        };
      }));
      toast.success(next ? 'Service activated' : 'Service retired');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update service');
    } finally {
      setSaving(s => ({ ...s, [key]: false }));
    }
  };

  if (loading) return <div className="text-sm text-gray-500">Loading taxonomy…</div>;

  return (
    <div className="space-y-3" data-testid="taxonomy-panel">
      {categories.map(cat => {
        const services = Object.entries(cat.services || {});
        const isOpen = !!expanded[cat.category_id];
        const activeCount = services.filter(([, s]) => s.active !== false).length;
        return (
          <div key={cat.category_id} className="border border-gray-200 rounded-lg bg-white" data-testid={`framework-cat-${cat.category_id}`}>
            <button
              onClick={() => setExpanded(e => ({ ...e, [cat.category_id]: !isOpen }))}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              data-testid={`framework-cat-toggle-${cat.category_id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                {isOpen ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                <div className="text-left min-w-0">
                  <div className="font-heading text-base font-semibold text-gray-900 truncate">{cat.label}</div>
                  <div className="text-xs text-gray-500 font-mono">
                    {cat.category_id} · {activeCount}/{services.length} services active
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                <span className={`text-xs font-mono ${cat.active ? 'text-emerald-700' : 'text-gray-400'}`}>
                  {cat.active ? 'ACTIVE' : 'RETIRED'}
                </span>
                <Switch
                  checked={!!cat.active}
                  onCheckedChange={(v) => toggleCategory(cat, v)}
                  disabled={saving[cat.category_id]}
                  data-testid={`framework-cat-switch-${cat.category_id}`}
                />
              </div>
            </button>

            {isOpen && (
              <div className="border-t border-gray-200 divide-y divide-gray-100">
                {services.map(([svcId, svc]) => {
                  const key = `${cat.category_id}:${svcId}`;
                  return (
                    <div key={svcId} className="flex items-center justify-between px-6 py-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{svc.label}</div>
                        <div className="text-xs text-gray-500 font-mono">{svcId}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-mono ${svc.active !== false ? 'text-emerald-700' : 'text-gray-400'}`}>
                          {svc.active !== false ? 'ACTIVE' : 'RETIRED'}
                        </span>
                        <Switch
                          checked={svc.active !== false}
                          onCheckedChange={(v) => toggleService(cat, svcId, v)}
                          disabled={saving[key]}
                          data-testid={`framework-svc-switch-${svcId}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Location rules — reusable geo constraint presets
// ---------------------------------------------------------------------------

const emptyRule = () => ({ type: 'state', state: 'NSW' });

const LocationRulesPanel = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {rule_id?, label, description, rules}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/framework/location-rules');
      setRules(res.data.rules || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing) return;
    if (!editing.label?.trim() || !editing.rules?.length) {
      toast.error('Give the preset a name and at least one rule');
      return;
    }
    try {
      const payload = {
        label: editing.label.trim(),
        description: editing.description || '',
        rules: editing.rules,
        active: editing.active ?? true,
      };
      if (editing.rule_id) {
        await api.patch(`/admin/framework/location-rules/${editing.rule_id}`, payload);
        toast.success('Preset updated');
      } else {
        await api.post('/admin/framework/location-rules', payload);
        toast.success('Preset created');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save preset');
    }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete "${rule.label}"?`)) return;
    try {
      await api.delete(`/admin/framework/location-rules/${rule.rule_id}`);
      toast.success('Preset deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div data-testid="locations-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-heading text-lg font-semibold text-gray-900">Reusable location presets</div>
          <div className="text-sm text-gray-600">Buyers attach presets to demand profiles. Matching evaluates all rules with OR.</div>
        </div>
        <Button
          onClick={() => setEditing({ label: '', description: '', rules: [emptyRule()], active: true })}
          className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
          data-testid="location-preset-new-btn"
        >
          <PlusCircle className="w-4 h-4 mr-2" /> New preset
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading presets…</div>
      ) : rules.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded-lg p-10 text-center text-sm text-gray-500" data-testid="location-preset-empty">
          No location presets yet. Create the first one to let buyers reuse geography rules.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rules.map(r => (
            <div key={r.rule_id} className="border border-gray-200 rounded-lg p-5 bg-white" data-testid={`location-preset-${r.rule_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-heading text-base font-semibold text-gray-900 truncate">{r.label}</div>
                  <div className="text-xs text-gray-500 font-mono truncate">{r.rule_id}</div>
                </div>
                <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${r.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                  {r.active ? 'ACTIVE' : 'OFF'}
                </span>
              </div>
              {r.description && (
                <p className="text-sm text-gray-600 mt-2 line-clamp-2">{r.description}</p>
              )}
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(r.rules || []).map((rr, i) => (
                  <span key={i} className="text-xs font-mono px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded">
                    {ruleSummary(rr)}
                  </span>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditing(JSON.parse(JSON.stringify(r)))}
                  className="border-gray-300 h-9"
                  data-testid={`location-preset-edit-${r.rule_id}`}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => remove(r)}
                  className="border-red-200 text-red-700 hover:bg-red-50 h-9"
                  data-testid={`location-preset-delete-${r.rule_id}`}
                >
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl" data-testid="location-preset-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.rule_id ? 'Edit location preset' : 'New location preset'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <LocationRuleEditor editing={editing} setEditing={setEditing} />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="location-preset-cancel">
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
            <Button onClick={save} className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="location-preset-save">
              <Save className="w-4 h-4 mr-2" /> Save preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ruleSummary = (r) => {
  switch (r.type) {
    case 'national':        return 'National';
    case 'state':           return `State: ${r.state}`;
    case 'postcode_list':   return `${(r.postcodes || []).length} postcodes`;
    case 'postcode_prefix': return `Prefix: ${(r.prefixes || []).join(', ')}`;
    case 'radius':          return `${r.radius_km || 0}km of ${r.center_postcode || '—'}`;
    default:                return r.type;
  }
};

const LocationRuleEditor = ({ editing, setEditing }) => {
  const update = (patch) => setEditing({ ...editing, ...patch });
  const updateRule = (i, patch) => {
    const next = editing.rules.slice();
    next[i] = { ...next[i], ...patch };
    update({ rules: next });
  };
  const removeRule = (i) => update({ rules: editing.rules.filter((_, ix) => ix !== i) });
  const addRule = () => update({ rules: [...editing.rules, emptyRule()] });

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs uppercase tracking-wider text-gray-500 font-mono mb-1">Name</label>
        <Input
          value={editing.label}
          onChange={(e) => update({ label: e.target.value })}
          placeholder="Sydney metro"
          data-testid="preset-label-input"
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wider text-gray-500 font-mono mb-1">Description (optional)</label>
        <Textarea
          value={editing.description || ''}
          onChange={(e) => update({ description: e.target.value })}
          placeholder="What kind of coverage this preset represents"
          rows={2}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs uppercase tracking-wider text-gray-500 font-mono">Rules (OR-combined)</label>
          <button onClick={addRule} className="text-xs text-[#0044CC] hover:text-[#0055FF] font-medium inline-flex items-center gap-1" data-testid="preset-add-rule">
            <PlusCircle className="w-3 h-3" /> Add rule
          </button>
        </div>
        <div className="space-y-2">
          {editing.rules.map((r, i) => (
            <div key={i} className="border border-gray-200 rounded p-3 bg-gray-50" data-testid={`preset-rule-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <Select value={r.type} onValueChange={(v) => updateRule(i, { type: v })}>
                  <SelectTrigger className="h-9 w-56 bg-white" data-testid={`preset-rule-type-${i}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RULE_TYPES.map(rt => (
                      <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button onClick={() => removeRule(i)} className="text-xs text-red-600 hover:underline ml-auto" data-testid={`preset-rule-remove-${i}`}>
                  Remove
                </button>
              </div>

              <RuleFields rule={r} onChange={(patch) => updateRule(i, patch)} idx={i} />
            </div>
          ))}
          {editing.rules.length === 0 && (
            <div className="text-xs text-gray-500 italic">Add at least one rule.</div>
          )}
        </div>
      </div>
    </div>
  );
};

const RuleFields = ({ rule, onChange, idx }) => {
  if (rule.type === 'national') {
    return <div className="text-xs text-gray-600">Matches any Australian lead.</div>;
  }
  if (rule.type === 'state') {
    return (
      <Select value={rule.state || 'NSW'} onValueChange={(v) => onChange({ state: v })}>
        <SelectTrigger className="h-9 w-40 bg-white" data-testid={`preset-rule-state-${idx}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {AU_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (rule.type === 'postcode_list') {
    return (
      <Input
        placeholder="2000, 2010, 2088"
        value={(rule.postcodes || []).join(', ')}
        onChange={(e) => onChange({ postcodes: parseList(e.target.value) })}
        data-testid={`preset-rule-postcodes-${idx}`}
      />
    );
  }
  if (rule.type === 'postcode_prefix') {
    return (
      <Input
        placeholder="20, 21, 22"
        value={(rule.prefixes || []).join(', ')}
        onChange={(e) => onChange({ prefixes: parseList(e.target.value) })}
        data-testid={`preset-rule-prefixes-${idx}`}
      />
    );
  }
  if (rule.type === 'radius') {
    return (
      <div className="space-y-2">
        <div className="text-[11px] text-gray-500">Type a suburb to auto-fill the postcode.</div>
        <div className="grid grid-cols-2 gap-2">
          <SuburbAutocomplete
            value={rule.center_label || ''}
            onChange={(v) => onChange({ center_label: v })}
            onSelect={(item) => onChange({
              center_label: `${item.suburb}, ${item.state}`,
              center_postcode: item.postcode,
              center_lat: item.lat,
              center_lng: item.lng,
            })}
            testid={`preset-rule-suburb-${idx}`}
          />
          <Input
            placeholder="Postcode"
            value={rule.center_postcode || ''}
            onChange={(e) => onChange({ center_postcode: e.target.value.replace(/\D/g, '').slice(0, 4) })}
            inputMode="numeric"
            maxLength={4}
            data-testid={`preset-rule-center-${idx}`}
          />
        </div>
        <Input
          type="number"
          placeholder="Radius km"
          value={rule.radius_km || ''}
          onChange={(e) => onChange({ radius_km: Number(e.target.value) || 0 })}
          data-testid={`preset-rule-radius-${idx}`}
        />
      </div>
    );
  }
  return null;
};

const parseList = (raw) =>
  (raw || '')
    .split(/[,\s]+/)
    .map(x => x.trim())
    .filter(Boolean);
